import globals from '@/globals';
import { pickBy } from 'lodash';
import LANG from '@/lang/eng';
import COLLECTIONS from './api_collections';

const reqOptions = {
  mode: 'cors',
  credentials: 'include',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  redirect: 'follow',
  referrer: 'no-referrer',
};

/**
 * Adds query parameters to the specified URL and returns a new URL (does not mutate the original URL).
 * If there are existing query parameters in the URL, the new params will be appended to the query.
 *
 * @param {*} url a URL object
 * @param {*} params dictionary of params
 * @returns
 */
function addSearchParams(url, params = {}) {
  const searchParams = new URLSearchParams([
    ...Array.from(url.searchParams.entries()),
    ...Object.entries(params),
  ]);
  const parts = [
    `${url.origin}${url.pathname}`,
    searchParams.toString(),
  ];
  return new URL(parts.filter(Boolean).join('?'));
}

/**
 * Concatenates the specified paths. Removes duplicate slashes(/) between path components
 * @param {*} a
 * @param {*} b
 * @returns
 */
function appendPath(a = '', b = '') {
  const core = [...a.split('/'), ...b.split('/')]
    .filter(Boolean)
    .join('/');
  const start = a.startsWith('/') ? '/' : '';
  const end = b.endsWith('/') ? '/' : '';
  return `${start}${core}${end}`;
}

/**
 * Resolves a URL based on a URI Prefix, a URI and a set of query params
 * @returns an URL object
 */
export function resolveURL(prefix = '', uri = '', params = {}, base = document.location) {
  try {
    const baseURL = new URL(`${prefix}${uri}`, base);
    return addSearchParams(baseURL, params);
  } catch (e) {
    console.error(`Cannot resolve URI: ${uri}`, e);
    return null;
  }
}

/**
 * Impure function to resolve the URL
 * Relies on implicit global dependencies: I.Util.uri, I.settings.system.prefix, jQuery
 * See: imperia/js/lib/util.js
 */
const { I } = globals;
// eslint-disable-next-line
function resolveURLImpure(prefix, uri, params = {}) {
  // the global prefix is implicitly used by I.Util.uri
  return I.Util.uri(uri, params);
}

/**
 * Resolves a `fetch-her` route to a standard set of uri and params
 * @param {*} routes collection of fetch-her routes
 * @param {*} id route identifier
 * @param {*} magicParams dictionary of query parameters, potentially including a magic `@path` param
 * @returns { uri, params, method, body } where uri is a standard imperia URI, params is a standard set of query params (no magic params)
 */
export function resolveRoute(routes = {}, id, magicParams = {}) {
  const route = routes[id] || {};
  const { method = 'GET' } = route;
  let uri = route.url;

  const pathComponent = magicParams['@path'];
  if (pathComponent) {
    uri = appendPath(uri, pathComponent);
  }

  // drop magic params such as `@path` from the query
  const params = pickBy(magicParams, (_, key) => !key.startsWith('@'));

  return {
    method,
    uri,
    ...(method === 'GET' && { params }),
    ...(method === 'POST' && { body: JSON.stringify(params) }),
  };
}

/**
 * Fix for https://jira.pirobase.de/jira/browse/ICMS-3801
 *
 * We are keeping the `fetch-her` Service interface for compatibility reasons
 * Under the hood, we are using the native fetch API
 *
 * TODO: remove fetch-her's `Collections API`
 * TODO: remove fetch-her's magic `@path` parameter
 * TODO: remove the FetchService API abstraction
 */
class MyFetchService {
  /**
   * @param {*} routeId route identifier
   * @param {*} magicParams dictionary of query params; Note: a magic `@path` parameter is also supported
   */
  GetData(routeId, magicParams) { // eslint-disable-line
    const { uri, params, body, method } = resolveRoute(COLLECTIONS, routeId, magicParams); // eslint-disable-line
    const prefix = I?.settings?.system?.prefix || '';
    const url = resolveURL(prefix, uri, params);
    if (!url) {
      return Promise.reject(new Error(`Cannot resolve URL for ${routeId}`));
    }

    return (
      fetch(url, { ...reqOptions, method, body })
        .then(async (response) => {
          // check for error response
          if (!response.ok) {
            // get error message from body or default to response status
            if (response.status === 414) {
              return Promise.reject(new Error(`${LANG.tooManyFiles}`));
            }

            return Promise.reject(new Error(`${LANG.responeReturnedStatusCode}: ${response.status}: ${response.statusText}`));
          }

          return response.json();
        })
        // mimics `fetch-her` error handling:
        // TODO: review this approach... it is probably better to throw the original error
        // so that consumers can deal with it
        .catch((err) => ({
          error: 1,
          message: err.message,
        }))
    );
  }
}

const fetchService = new MyFetchService();
export default fetchService;
